/* Collection/search colour-card filtering. Liquid renders the correct default first paint. */
(() => {
  const pageSize = 24;
  const fill = (pattern, values) => (pattern || '').replace(/\{(\w+)\}|\[\[(\w+)\]\]/g, (_, a, b) => values[a || b] ?? '');
  // Kept equivalent to src/lib/wings/catalog.ts: every term across both titles,
  // product type and tags; preferred-language title hits precede catalogue order.
  const normaliseSearch = (text) => String(text || '').normalize('NFKD')
    .replace(/[̀-ͯ]/g, '').replace(/[ً-ٰٟـ]/g, '').replace(/[أإآٱ]/g, 'ا')
    .replace(/ى/g, 'ي').replace(/ة/g, 'ه').toLowerCase();
  const compactSearch = (text) => text.replace(/[^\p{L}\p{N}]+/gu, '');
  const matchesSearch = (haystack, term) => haystack.includes(term)
    || (compactSearch(term).length > 0 && compactSearch(haystack).includes(compactSearch(term)));
  const rankSearch = (documents, query, locale) => {
    const terms = normaliseSearch(query).split(/\s+/).filter(Boolean);
    if (!terms.length) return [];
    return documents.map((document, index) => {
      const en = normaliseSearch(document.titles.en || document.title);
      const ar = normaliseSearch(document.titles.ar || document.title);
      const all = [en, ar, normaliseSearch(document.type), ...(document.tags || []).map(normaliseSearch)].join(' | ');
      return { document, index, all, preferred: locale === 'ar' ? ar || en : en };
    }).filter((hit) => terms.every((term) => matchesSearch(hit.all, term)))
      .sort((a, b) => Number(!terms.every((term) => matchesSearch(a.preferred, term)))
        - Number(!terms.every((term) => matchesSearch(b.preferred, term)))
        || Number(b.document.published || 0) - Number(a.document.published || 0) || a.index - b.index)
      .map((hit) => hit.document.handle);
  };
  window.WingsSearch = { normalise: normaliseSearch, rank: rankSearch };
  const searchCache = new Map();
  const loadSearchLocale = (basePath, sectionId, withCards) => {
    const key = `${basePath}|${sectionId}`;
    if (searchCache.has(key)) return searchCache.get(key);
    const promise = (async () => {
      const documents = [];
      const cards = [];
      let next = 1;
      while (next) {
        const url = new URL(basePath, window.location.href);
        if (url.origin !== new URL(window.location.href).origin) throw new Error('Invalid catalogue origin');
        url.search = '';
        url.searchParams.set('section_id', sectionId);
        if (next > 1) url.searchParams.set('page', String(next));
        const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Catalogue unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const index = html.querySelector('[data-search-index]');
        if (!index) throw new Error('Catalogue index missing');
        documents.push(...JSON.parse(index.querySelector('[data-search-documents]').textContent));
        if (withCards) cards.push(...index.querySelectorAll('[data-product-card]'));
        const following = Number(index.dataset.nextPage || 0);
        next = following > next ? following : 0;
      }
      return { documents, cards };
    })();
    searchCache.set(key, promise);
    promise.catch(() => searchCache.delete(key));
    return promise;
  };
  const prepareSearch = async (container) => {
    const currentLocale = document.documentElement.lang.split('-')[0];
    const current = await loadSearchLocale(container.dataset.searchCatalogueUrl, 'search-catalogue', true);
    const documents = new Map(current.documents.map((item) => [item.handle, { ...item, titles: { [currentLocale]: item.title } }]));
    const languages = JSON.parse(container.querySelector('[data-search-languages]')?.textContent || '[]');
    // Metadata-only alternate locale reads retain bilingual matching after store
    // translations are added, without duplicating the product-card HTML or IDs.
    const alternates = languages.filter((language) => language.locale !== currentLocale && ['en', 'ar'].includes(language.locale));
    await Promise.all(alternates.map(async (language) => {
      const path = `${language.root.replace(/\/$/, '')}/collections/all`;
      const alternate = await loadSearchLocale(path, 'search-index', false);
      alternate.documents.forEach((item) => {
        const target = documents.get(item.handle);
        if (target) {
          target.titles[language.locale] = item.title;
          target.tags = [...new Set([...(target.tags || []), ...(item.tags || [])])];
          if (!target.type) target.type = item.type;
        }
      });
    }));
    container.searchDocuments = [...documents.values()];
    const grid = container.querySelector('[data-collection-grid]');
    grid.replaceChildren(...current.cards.map((card) => card.cloneNode(true)));
    container.dataset.serverPage = '1';
  };
  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-collection]').forEach((container) => {
      if (container.dataset.initialized) return;
      container.dataset.initialized = 'true';
      if (container.dataset.searchCatalogueUrl && !container.dataset.catalogueReady) {
        container.setAttribute('aria-busy', 'true');
        prepareSearch(container).catch(() => { /* Native Shopify results remain the no-JS/network-failure fallback. */ }).finally(() => {
          container.dataset.catalogueReady = 'true';
          delete container.dataset.initialized;
          container.removeAttribute('aria-busy');
          initialise(container.parentElement || document);
          document.dispatchEvent(new CustomEvent('shopify:section:load'));
        });
        return;
      }
      // Shopify reserves ?page= for its 250-product server pagination. A shared
      // client colour-card page must recover page one before applying its 24-card slice.
      if (Number(container.dataset.serverPage || 1) > 1 && container.dataset.sectionId) {
        const recover = async () => {
          container.setAttribute('aria-busy', 'true');
          container.querySelector('[data-collection-retry]')?.remove();
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete('page');
            url.searchParams.set('section_id', container.dataset.sectionId);
            const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error('Collection section unavailable');
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const replacement = html.querySelector('[data-collection]');
            if (!replacement) throw new Error('Collection section missing');
            const parent = container.parentElement;
            container.replaceWith(replacement);
            initialise(parent || document);
            document.dispatchEvent(new CustomEvent('shopify:section:load'));
          } catch {
            const retry = document.createElement('div');
            retry.dataset.collectionRetry = '';
            retry.className = 'commerce-form-error';
            retry.setAttribute('role', 'status');
            const message = document.createElement('p');
            message.textContent = container.dataset.loadError;
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'button button--outline';
            button.textContent = container.dataset.retryLabel;
            button.addEventListener('click', recover);
            retry.append(message, button);
            container.append(retry);
            container.removeAttribute('aria-busy');
          }
        };
        recover();
        return;
      }
      const grid = container.querySelector('[data-collection-grid]');
      if (!grid) return;
      const cards = Array.from(grid.querySelectorAll('[data-product-card]'));
      const originalOrder = new Map(cards.map((card, index) => [card, index]));
      const filterLinks = Array.from(container.querySelectorAll('[data-filter]'));
      const allowedFilters = new Set(['all', ...filterLinks.map((link) => link.dataset.filter)]);
      const defaultFilter = container.dataset.defaultFilter || 'all';
      allowedFilters.add(defaultFilter);
      // Keep derived filter links usable even when no chip exists (for example an empty result).
      cards.forEach((card) => (card.dataset.filters || '').split(/\s+/).forEach((key) => allowedFilters.add(key)));
      const nav = container.querySelector('[data-collection-pagination]');
      const count = container.querySelector('[data-collection-count]');
      const empty = container.querySelector('[data-collection-empty]');
      const formatter = new Intl.NumberFormat((document.documentElement.lang || 'en').startsWith('ar') ? 'ar-QA-u-nu-latn' : 'en-QA');
      const pluralRules = new Intl.PluralRules(document.documentElement.lang || 'en');
      let activeFilter = defaultFilter;
      let activePage = 1;
      let pageCount = 1;
      const pageText = (page) => fill(container.dataset.pageLabel || '{n} / {total}', {
        n: formatter.format(page), total: formatter.format(pageCount),
      });
      const hrefFor = (page, filter = activeFilter) => {
        const url = new URL(window.location.href);
        if (filter === defaultFilter) url.searchParams.delete('filter');
        else url.searchParams.set('filter', filter);
        if (page > 1) url.searchParams.set('page', String(page));
        else url.searchParams.delete('page');
        return `${url.pathname}${url.search}${url.hash}`;
      };
      const pageLink = (page, label, className, ariaLabel) => {
        const link = document.createElement('a');
        link.href = hrefFor(page);
        link.dataset.page = String(page);
        link.className = className;
        link.textContent = label;
        if (ariaLabel) link.setAttribute('aria-label', ariaLabel);
        if (page < 1 || page > pageCount) {
          link.setAttribute('aria-disabled', 'true');
          link.removeAttribute('href');
          link.removeAttribute('data-page');
        }
        return link;
      };
      const renderPagination = () => {
        if (!nav) return;
        nav.replaceChildren();
        nav.hidden = pageCount <= 1;
        if (pageCount <= 1) return;
        const row = document.createElement('div');
        row.className = 'collection-pagination__row';
        const previous = pageLink(activePage - 1, '‹', 'collection-pagination__arrow', container.dataset.previous);
        previous.rel = 'prev';
        row.append(previous);
        const neighbours = matchMedia('(min-width: 640px)').matches ? 1 : 0;
        const shown = Array.from({ length: pageCount }, (_, i) => i + 1)
          .filter((page) => page === 1 || page === pageCount || Math.abs(page - activePage) <= neighbours);
        let last = 0;
        shown.forEach((page) => {
          if (last && page > last + 1) {
            const gap = document.createElement('span');
            gap.className = 'collection-pagination__gap';
            gap.textContent = '…';
            gap.setAttribute('aria-hidden', 'true');
            row.append(gap);
          }
          const link = pageLink(page, formatter.format(page), 'collection-pagination__number', pageText(page));
          if (page === activePage) link.setAttribute('aria-current', 'page');
          row.append(link);
          last = page;
        });
        const next = pageLink(activePage + 1, '›', 'collection-pagination__arrow', container.dataset.next);
        next.rel = 'next';
        row.append(next);
        const status = document.createElement('p');
        status.textContent = pageText(activePage);
        status.setAttribute('aria-hidden', 'true');
        nav.append(row, status);
      };
      const apply = ({ fromUrl = false, replace = false, scroll = false } = {}) => {
        if (fromUrl) {
          const url = new URL(window.location.href);
          const requested = url.searchParams.get('filter');
          activeFilter = requested === null ? defaultFilter : allowedFilters.has(requested) ? requested : 'all';
          activePage = Math.max(1, Number.parseInt(url.searchParams.get('page') || '1', 10) || 1);
        }
        const query = container.searchDocuments ? (new URL(window.location.href).searchParams.get('q') || '').trim().slice(0, 200) : null;
        const searchOrder = query !== null ? new Map(rankSearch(container.searchDocuments, query, document.documentElement.lang.split('-')[0]).map((handle, index) => [handle, index])) : null;
        const filtered = cards.filter((card) => (!searchOrder || searchOrder.has(card.dataset.handle)) && (activeFilter === 'all' || (card.dataset.filters || '').split(/\s+/).includes(activeFilter)));
        filtered.sort((a, b) => searchOrder ? searchOrder.get(a.dataset.handle) - searchOrder.get(b.dataset.handle) || originalOrder.get(a) - originalOrder.get(b) : activeFilter === 'bestsellers'
          ? Number(a.dataset.rank) - Number(b.dataset.rank) || originalOrder.get(a) - originalOrder.get(b)
          : originalOrder.get(a) - originalOrder.get(b));
        pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
        activePage = Math.min(activePage, pageCount);
        const shownCards = new Set(filtered.slice((activePage - 1) * pageSize, activePage * pageSize));
        cards.forEach((card) => { card.hidden = !shownCards.has(card); });
        // Appending existing nodes retains the dialog, selected size and event handlers.
        const fragment = document.createDocumentFragment();
        filtered.forEach((card) => fragment.append(card));
        cards.filter((card) => !filtered.includes(card)).forEach((card) => fragment.append(card));
        grid.append(fragment);
        filterLinks.forEach((link) => {
          if (link.dataset.filter === activeFilter && !link.hasAttribute('data-clear-filters')) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
          link.href = hrefFor(1, link.dataset.filter === activeFilter ? 'all' : link.dataset.filter);
        });
        const clear = container.querySelector('[data-clear-filters]');
        if (clear) clear.hidden = activeFilter === 'all';
        const total = container.hasAttribute('data-count-colors') ? filtered.length : new Set(filtered.map((card) => card.dataset.handle)).size;
        const pluralCategory = pluralRules.select(total);
        const pluralKey = `results${pluralCategory[0].toUpperCase()}${pluralCategory.slice(1)}`;
        if (count) count.textContent = fill(container.dataset[pluralKey] || container.dataset.resultsOther, { count: formatter.format(total) });
        if (query !== null) {
          const field = container.querySelector('input[name="q"]');
          if (field && field.value !== query) field.value = query;
          if (count) {
            count.hidden = !query;
            if (query && total === 0) count.textContent = container.dataset.searchNoResults;
          }
          const shopLink = container.querySelector('[data-search-empty-action]');
          if (shopLink) shopLink.hidden = !query || total > 0;
        }
        if (empty) empty.hidden = total > 0;
        renderPagination();
        if (replace) history.replaceState(null, '', hrefFor(activePage));
        if (scroll) {
          const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
          container.scrollIntoView({ behavior: reduced ? 'instant' : 'smooth', block: 'start' });
        }
      };
      container.addEventListener('click', (event) => {
        const link = event.target.closest('[data-filter], [data-page]');
        if (!link || !container.contains(link) || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (link.hasAttribute('data-filter')) {
          const requested = link.dataset.filter;
          activeFilter = requested === activeFilter ? 'all' : requested;
          activePage = 1;
          apply({ replace: true });
        } else {
          activePage = Number(link.dataset.page);
          apply({ replace: true, scroll: true });
        }
      });
      if (container.searchDocuments) container.querySelector('form[role="search"]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        const field = container.querySelector('input[name="q"]');
        const query = field.value.trim().slice(0, 200);
        const url = new URL(window.location.href);
        url.searchParams.set('q', query);
        url.searchParams.set('type', 'product');
        url.searchParams.delete('page');
        url.searchParams.delete('filter');
        history.pushState(null, '', `${url.pathname}${url.search}`);
        apply({ fromUrl: true });
      });
      window.addEventListener('popstate', () => apply({ fromUrl: true }));
      matchMedia('(min-width: 640px)').addEventListener('change', renderPagination);
      apply({ fromUrl: true });
    });
  };
  initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
